import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const parseLocalDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput === 'string') {
        if (dateInput.includes('T')) {
            const dateObj = new Date(dateInput);
            return new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * 60000);
        }
        const parts = dateInput.split('T')[0].split('-');
        if (parts.length === 3) {
            return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        }
    }
    return new Date(dateInput);
};


// Colores de badge por tipo de objeto
const OBJETO_BADGE = {
    'Proyecto':   'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    'Rendición':  'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    'Adenda':     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};
const OBJETO_BADGE_DEFAULT = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

const TramitacionExternaRow = ({ tramitacion = {}, onUpdate, showHeaderLabels = false }) => {
    const { id, exp, objeto } = tramitacion;

    // Fecha Ingreso: solo se completa en filas de Proyecto y Adenda,
    // pero la columna siempre existe para mantener los inputs alineados entre filas
    const esProyectoOAdenda = objeto === 'Proyecto' || objeto === 'Adenda';
    const gridCols = 'grid-cols-6';

    const handleDateChange = (date, field) => {
        if (!tramitacion) return;
        onUpdate(id, { [field]: date });
    };

    const handleExpChange = (e) => {
        onUpdate(id, { exp: e.target.value });
    };

    const handleObjetoChange = (e) => {
        onUpdate(id, { objeto: e.target.value });
    };

    const inputClasses = "w-full p-2 border border-gray-300 rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all";
    const labelClasses = "block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 uppercase tracking-wide";
    const badgeClass = OBJETO_BADGE[objeto] || OBJETO_BADGE_DEFAULT;

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm mb-2">
            {/* Header con Expediente + badge de Objeto — solo en la primera fila */}
            {showHeaderLabels && (
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                    <div className={`grid ${gridCols} gap-4`}>
                        <div className={labelClasses}>Expediente</div>
                        <div className={labelClasses}>Fecha Ingreso</div>
                        <div className={labelClasses}>Salida convenio</div>
                        <div className={labelClasses}>Firma convenio</div>
                        <div className={labelClasses}>Salida resolución</div>
                        <div className={labelClasses}>Firma resolución</div>
                    </div>
                </div>
            )}

            {/* Fila de datos */}
            <div className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors px-4 py-3">
                <div className={`grid ${gridCols} gap-4 items-center`}>

                    {/* Expediente + badge de objeto */}
                    <div className="flex flex-col gap-1">
                        <input
                            type="text"
                            id={`exp-${id}`}
                            value={exp || ''}
                            onChange={handleExpChange}
                            className={`${inputClasses} bg-gray-50 dark:bg-gray-600 cursor-default`}
                            placeholder="N° expediente"
                            readOnly
                        />
                        {objeto && (
                            <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full w-fit ${badgeClass}`}>
                                {objeto}
                            </span>
                        )}
                        {/* Campo objeto oculto para mantener el estado */}
                        <input type="hidden" id={`objeto-${id}`} value={objeto || ''} onChange={handleObjetoChange} />
                    </div>

                    {/* Fecha Ingreso — solo editable en Proyecto y Adenda; espacio vacío en el resto para alinear */}
                    <div>
                        {esProyectoOAdenda ? (
                            <DatePicker
                                id={`fecha-ingreso-${id}`}
                                selected={parseLocalDate(tramitacion?.fecha_ingreso)}
                                onChange={(date) => handleDateChange(date, 'fecha_ingreso')}
                                dateFormat="dd/MM/yyyy"
                                className={inputClasses}
                                placeholderText="DD/MM/AAAA"
                                isClearable
                            />
                        ) : (
                            <div className="w-full p-2" />
                        )}
                    </div>


                    {/* Fecha Salida Convenio */}
                    <div>
                        <DatePicker
                            selected={parseLocalDate(tramitacion?.fecha_salida_convenio)}
                            onChange={(date) => handleDateChange(date, 'fecha_salida_convenio')}
                            dateFormat="dd/MM/yyyy"
                            className={inputClasses}
                            placeholderText="DD/MM/AAAA"
                            isClearable
                        />
                    </div>

                    {/* Fecha Firma Convenio */}
                    <div>
                        <DatePicker
                            id={`fecha-firma-convenio-${id}`}
                            selected={parseLocalDate(tramitacion?.fecha_firma_convenio)}
                            onChange={(date) => handleDateChange(date, 'fecha_firma_convenio')}
                            dateFormat="dd/MM/yyyy"
                            className={inputClasses}
                            placeholderText="DD/MM/AAAA"
                            isClearable
                        />
                    </div>

                    {/* Fecha Salida Resolución */}
                    <div>
                        <DatePicker
                            id={`fecha-salida-reso-${id}`}
                            selected={parseLocalDate(tramitacion?.fecha_salida_reso)}
                            onChange={(date) => handleDateChange(date, 'fecha_salida_reso')}
                            dateFormat="dd/MM/yyyy"
                            className={inputClasses}
                            placeholderText="DD/MM/AAAA"
                            isClearable
                        />
                    </div>

                    {/* Fecha Firma Resolución */}
                    <div>
                        <DatePicker
                            id={`fecha-firma-reso-${id}`}
                            selected={parseLocalDate(tramitacion?.fecha_firma_reso)}
                            onChange={(date) => handleDateChange(date, 'fecha_firma_reso')}
                            dateFormat="dd/MM/yyyy"
                            className={inputClasses}
                            placeholderText="DD/MM/AAAA"
                            isClearable
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TramitacionExternaRow;